import { useEffect, useLayoutEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import VeloraLanding from "@/pages/VeloraLanding";
import AdminPage from "@/pages/AdminPage";
import OurStoryPage from "@/pages/OurStoryPage";
import CataloguePage from "@/pages/CataloguePage";
import InstagramRedirectPage from "@/pages/InstagramRedirectPage";
import LandingPage from "@/pages/landings/LandingPage";
import FounderPage from "@/pages/FounderPage";
import BlogIndexPage from "@/pages/blog/BlogIndexPage";
import BlogPostPage from "@/pages/blog/BlogPostPage";
import InfoPage from "@/pages/info/InfoPage";
import CorridorPage from "@/pages/corridors/CorridorPage";
import StoryPrompt from "@/components/ledger/StoryPrompt";
import MastheadReturn from "@/components/ledger/MastheadReturn";
import LanguagePicker from "@/components/i18n/LanguagePicker";
import LanguageSuggestionToast from "@/components/i18n/LanguageSuggestionToast";
import FloatingMenu from "@/components/velora/FloatingMenu";
import { Toaster } from "@/components/ui/sonner";
import "@/App.css";

/** Renders the i18n controls everywhere except /admin. */
function I18nControls() {
  const { pathname } = useLocation();
  if (pathname === "/admin" || pathname === "/ig") return null;
  return (
    <>
      <LanguageSuggestionToast />
      <LanguagePicker />
    </>
  );
}

/** Floating action menu - auto-opens for 2s on first visit, sits above
 *  the WhatsApp button. Hidden on /admin. */
function FloatingActions() {
  const { pathname } = useLocation();
  if (pathname === "/admin" || pathname === "/ig") return null;
  return <FloatingMenu />;
}

function PublicStoryPrompt() {
  const { pathname } = useLocation();
  if (pathname === "/admin" || pathname === "/ig") return null;
  return <StoryPrompt />;
}

function PublicMastheadReturn() {
  const { pathname } = useLocation();
  if (pathname === "/admin" || pathname === "/ig") return null;
  return <MastheadReturn />;
}

/** While the user is actively scrolling on mobile, flag the body so all
 *  floating action buttons fade out (CSS in App.css). Reduces accidental
 *  thumb taps mid-scroll. Restores ~400ms after scrolling pauses. */
function useScrollIdleHide() {
  useEffect(() => {
    let timer;
    const onScroll = () => {
      document.body.dataset.scrolling = "1";
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        delete document.body.dataset.scrolling;
      }, 400);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.clearTimeout(timer);
      delete document.body.dataset.scrolling;
    };
  }, []);
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useLayoutEffect(() => {
    if (hash) return;
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname, hash]);

  return null;
}

function App() {
  useScrollIdleHide();
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<VeloraLanding />} />
          <Route path="/our-story" element={<OurStoryPage />} />
          <Route path="/catalogue" element={<CataloguePage />} />
          {/* Keyword-targeted SEO landing pages */}
          <Route path="/rice-exporter-from-india" element={<LandingPage slug="rice-exporter" />} />
          <Route path="/basmati-rice-exporter-from-india" element={<LandingPage slug="basmati" />} />
          <Route path="/non-basmati-rice-exporter-india" element={<LandingPage slug="non-basmati" />} />
          <Route path="/pulses-supplier-from-india" element={<LandingPage slug="pulses" />} />
          <Route path="/spices-exporter-from-india" element={<LandingPage slug="spices" />} />
          <Route path="/dry-fruits-exporter-from-india" element={<LandingPage slug="dry-fruits" />} />
          <Route path="/fresh-produce-exporter-from-india" element={<LandingPage slug="fresh-produce" />} />
          <Route path="/mango-exporter-from-india" element={<LandingPage slug="mango" />} />
          <Route path="/indian-agro-products-exporter" element={<LandingPage slug="indian-agro" />} />
          {/* Long-tail SEO landing pages — aggressive commercial keyword targeting */}
          <Route path="/premium-rice-supplier-india" element={<LandingPage slug="premium-rice" />} />
          <Route path="/1121-basmati-rice-exporter" element={<LandingPage slug="1121-basmati" />} />
          <Route path="/1509-basmati-rice-exporter" element={<LandingPage slug="1509-basmati" />} />
          <Route path="/sortex-cleaned-rice-exporter" element={<LandingPage slug="sortex-rice" />} />
          <Route path="/wholesale-rice-supplier-from-india" element={<LandingPage slug="wholesale-rice" />} />
          {/* Wave 2: generic-search commercial keyword landings */}
          <Route path="/1121-basmati-rice-supplier-from-india" element={<LandingPage slug="1121-supplier" />} />
          <Route path="/bulk-non-basmati-rice-exporter-india" element={<LandingPage slug="bulk-non-basmati" />} />
          <Route path="/private-label-rice-exporter-india" element={<LandingPage slug="private-label-rice" />} />
          <Route path="/wholesale-pulses-supplier-from-india" element={<LandingPage slug="wholesale-pulses" />} />
          <Route path="/turmeric-powder-exporter-from-india" element={<LandingPage slug="turmeric-powder" />} />
          <Route path="/cumin-seeds-exporter-from-india" element={<LandingPage slug="cumin-seeds" />} />
          <Route path="/red-chilli-exporter-from-india" element={<LandingPage slug="red-chilli" />} />
          <Route path="/coriander-powder-exporter-from-india" element={<LandingPage slug="coriander-powder" />} />
          <Route path="/fennel-seeds-exporter-from-india" element={<LandingPage slug="fennel-seeds" />} />
          <Route path="/black-pepper-exporter-from-india" element={<LandingPage slug="black-pepper" />} />
          <Route path="/sesame-seeds-exporter-from-india" element={<LandingPage slug="sesame-seeds" />} />
          <Route path="/cashew-nuts-exporter-from-india" element={<LandingPage slug="cashew-nuts" />} />
          <Route path="/toor-dal-exporter-from-india" element={<LandingPage slug="toor-dal" />} />
          <Route path="/sona-masuri-rice-exporter-india" element={<LandingPage slug="sona-masuri" />} />
          <Route path="/kesar-mango-exporter-from-india" element={<LandingPage slug="kesar-mango" />} />
          <Route path="/alphonso-mango-exporter-from-india" element={<LandingPage slug="alphonso-mango" />} />
          {/* Wave 3: variety × processing matrix (SKRM-style) */}
          <Route path="/1121-raw-basmati-rice" element={<LandingPage slug="1121-raw" />} />
          <Route path="/1121-steam-basmati-rice" element={<LandingPage slug="1121-steam" />} />
          <Route path="/1121-sella-basmati-rice" element={<LandingPage slug="1121-sella" />} />
          <Route path="/1121-golden-sella-basmati-rice" element={<LandingPage slug="1121-golden-sella" />} />
          <Route path="/1509-raw-basmati-rice" element={<LandingPage slug="1509-raw" />} />
          <Route path="/1509-steam-basmati-rice" element={<LandingPage slug="1509-steam" />} />
          <Route path="/1509-sella-basmati-rice" element={<LandingPage slug="1509-sella" />} />
          <Route path="/1509-golden-sella-basmati-rice" element={<LandingPage slug="1509-golden-sella" />} />
          <Route path="/1718-raw-basmati-rice" element={<LandingPage slug="1718-raw" />} />
          <Route path="/1718-steam-basmati-rice" element={<LandingPage slug="1718-steam" />} />
          <Route path="/1718-sella-basmati-rice" element={<LandingPage slug="1718-sella" />} />
          <Route path="/1718-golden-sella-basmati-rice" element={<LandingPage slug="1718-golden-sella" />} />
          <Route path="/pusa-basmati-rice-exporter" element={<LandingPage slug="pusa-basmati-supply" />} />
          <Route path="/pusa-steam-basmati-rice" element={<LandingPage slug="pusa-steam" />} />
          <Route path="/pusa-sella-basmati-rice" element={<LandingPage slug="pusa-sella" />} />
          <Route path="/pusa-golden-sella-basmati-rice" element={<LandingPage slug="pusa-golden-sella" />} />
          {/* Wave 5: non-basmati variety × processing matrix */}
          <Route path="/pr11-non-basmati-rice" element={<LandingPage slug="pr11-overview" />} />
          <Route path="/pr11-raw-non-basmati-rice" element={<LandingPage slug="pr11-raw" />} />
          <Route path="/pr11-steam-non-basmati-rice" element={<LandingPage slug="pr11-steam" />} />
          <Route path="/pr11-sella-non-basmati-rice" element={<LandingPage slug="pr11-sella" />} />
          <Route path="/pr11-golden-sella-non-basmati-rice" element={<LandingPage slug="pr11-golden-sella" />} />
          <Route path="/pr14-non-basmati-rice" element={<LandingPage slug="pr14-overview" />} />
          <Route path="/pr14-raw-non-basmati-rice" element={<LandingPage slug="pr14-raw" />} />
          <Route path="/pr14-steam-non-basmati-rice" element={<LandingPage slug="pr14-steam" />} />
          <Route path="/pr14-sella-non-basmati-rice" element={<LandingPage slug="pr14-sella" />} />
          <Route path="/pr14-golden-sella-non-basmati-rice" element={<LandingPage slug="pr14-golden-sella" />} />
          <Route path="/parmal-non-basmati-rice" element={<LandingPage slug="parmal-overview" />} />
          <Route path="/parmal-raw-non-basmati-rice" element={<LandingPage slug="parmal-raw" />} />
          <Route path="/parmal-steam-non-basmati-rice" element={<LandingPage slug="parmal-steam" />} />
          <Route path="/parmal-sella-non-basmati-rice" element={<LandingPage slug="parmal-sella" />} />
          <Route path="/parmal-golden-sella-non-basmati-rice" element={<LandingPage slug="parmal-golden-sella" />} />
          <Route path="/ir64-rice-exporter" element={<LandingPage slug="ir64-overview" />} />
          <Route path="/ir64-raw-rice" element={<LandingPage slug="ir64-raw" />} />
          <Route path="/ir64-steam-rice" element={<LandingPage slug="ir64-steam" />} />
          <Route path="/ir64-sella-rice" element={<LandingPage slug="ir64-sella" />} />
          <Route path="/ir64-golden-sella-rice" element={<LandingPage slug="ir64-golden-sella" />} />
          <Route path="/sona-masuri-raw-rice" element={<LandingPage slug="sona-masuri-raw" />} />
          <Route path="/sona-masuri-steam-rice" element={<LandingPage slug="sona-masuri-steam" />} />
          <Route path="/sona-masuri-sella-rice" element={<LandingPage slug="sona-masuri-sella" />} />

          {/* 2026-06-01 gap-fill: 1401 basmati matrix */}
          <Route path="/1401-basmati-rice-exporter" element={<LandingPage slug="1401-overview" />} />
          <Route path="/1401-raw-basmati-rice" element={<LandingPage slug="1401-raw" />} />
          <Route path="/1401-steam-basmati-rice" element={<LandingPage slug="1401-steam" />} />
          <Route path="/1401-sella-basmati-rice" element={<LandingPage slug="1401-sella" />} />
          <Route path="/1401-golden-sella-basmati-rice" element={<LandingPage slug="1401-golden-sella" />} />

          {/* Sugandha basmati matrix */}
          <Route path="/sugandha-basmati-rice-exporter" element={<LandingPage slug="sugandha-overview" />} />
          <Route path="/sugandha-raw-basmati-rice" element={<LandingPage slug="sugandha-raw" />} />
          <Route path="/sugandha-steam-basmati-rice" element={<LandingPage slug="sugandha-steam" />} />
          <Route path="/sugandha-sella-basmati-rice" element={<LandingPage slug="sugandha-sella" />} />
          <Route path="/sugandha-golden-sella-basmati-rice" element={<LandingPage slug="sugandha-golden-sella" />} />

          {/* Sharbati basmati matrix */}
          <Route path="/sharbati-basmati-rice-exporter" element={<LandingPage slug="sharbati-overview" />} />
          <Route path="/sharbati-raw-basmati-rice" element={<LandingPage slug="sharbati-raw" />} />
          <Route path="/sharbati-steam-basmati-rice" element={<LandingPage slug="sharbati-steam" />} />
          <Route path="/sharbati-sella-basmati-rice" element={<LandingPage slug="sharbati-sella" />} />
          <Route path="/sharbati-golden-sella-basmati-rice" element={<LandingPage slug="sharbati-golden-sella" />} />

          {/* Traditional basmati + white-sella + pusa-raw */}
          <Route path="/traditional-basmati-rice-exporter" element={<LandingPage slug="traditional-overview" />} />
          <Route path="/traditional-raw-basmati-rice" element={<LandingPage slug="traditional-raw" />} />
          <Route path="/traditional-sella-basmati-rice" element={<LandingPage slug="traditional-sella" />} />
          <Route path="/white-sella-basmati-rice" element={<LandingPage slug="white-sella" />} />
          <Route path="/pusa-raw-basmati-rice" element={<LandingPage slug="pusa-raw" />} />

          {/* Pesticide-free hub, price guides, and rice-supplier authority hub */}
          <Route path="/pesticide-free-basmati-rice-exporter" element={<LandingPage slug="pesticide-free" />} />
          <Route path="/basmati-rice-price" element={<LandingPage slug="basmati-price" />} />
          <Route path="/indian-rice-price" element={<LandingPage slug="indian-rice-price" />} />
          <Route path="/rice-supplier-in-india" element={<LandingPage slug="rice-supplier-india" />} />

          {/* Country corridor pages — 36 destination markets */}
          <Route path="/basmati-rice/export-from-india-to-:slug" element={<CorridorPage />} />
          {/* Founder brand-keyword page */}
          <Route path="/founder/darshit-anadkat" element={<FounderPage />} />
          {/* Blog / Journal */}
          <Route path="/blog" element={<BlogIndexPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          {/* Credibility / depth pages */}
          <Route path="/infrastructure" element={<InfoPage slug="infrastructure" />} />
          <Route path="/quality-assurance" element={<InfoPage slug="quality-assurance" />} />
          <Route path="/our-process" element={<InfoPage slug="our-process" />} />
          <Route path="/certifications" element={<InfoPage slug="certifications" />} />
          <Route path="/ig" element={<InstagramRedirectPage />} />
          <Route path="/admin" element={<AdminPage />} />
        </Routes>
        <PublicStoryPrompt />
        <I18nControls />
        <FloatingActions />
        <PublicMastheadReturn />
      </BrowserRouter>
      <Toaster
        position="bottom-right"
        theme="dark"
        toastOptions={{
          style: {
            background: "#FFFFFF",
            border: "1px solid rgba(232,148,58,0.3)",
            color: "#3D1F00",
            fontFamily: "Inter, sans-serif",
            letterSpacing: "0.03em",
          },
        }}
      />
    </div>
  );
}

export default App;
